import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Form, Spinner } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import { showSuccess, showError } from "utils/swalHelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import { autocompleteDropdownStyles } from "Constants/Style";
import "./CreateReorderLevelModal.css";

interface Props {
  show: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

interface FormValues {
  category: any | null;
  subCategory: any | null;
  item: any | null;
  reorderLevel: string;
}

const commonInputStyle = {
  fontSize: "0.8rem",
  minHeight: 38,
  padding: "0.375rem 0.75rem",
};

const commonLabelStyle = {
  fontSize: "0.8rem",
  marginBottom: 6,
  marginTop: 12,
};

const CreateReorderLevelModal: React.FC<Props> = ({ show, onClose, onCreated }) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    category: Yup.mixed().required("Category is required"),
    subCategory: Yup.mixed().required("Sub Category is required"),
    item: Yup.mixed().required("Item is required"),
    reorderLevel: Yup.string()
      .required("Reorder Level is required")
      .matches(/^\d+(\.\d+)?$/, "Enter a valid number"),
  });

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      category: null,
      subCategory: null,
      item: null,
      reorderLevel: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (show) fetchCategories();
    return () => {
      if (!show) {
        formik.resetForm();
        setSubCategories([]);
        setItems([]);
      }
    };
  }, [show]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await Axios.get(`${api.master.category.dropdown}`);
      const resp = res?.data;
      let list: any[] = [];
      if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.payload)) list = res.data.payload;
      else if (resp && typeof resp === "object") list = Object.values(resp);
      // Normalize to objects with `id` and `name` so selects always send id
      const normalized = (list || []).map((it: any) => ({
        id: it?.id ?? it?.category_id ?? it?.value ?? it?.key ?? "",
        name:
          it?.name ?? it?.category_name ?? it?.label ?? it?.category_name ??
          (typeof it === "string" ? it : ""),
        raw: it,
      }));
      setCategories(normalized);
    } catch (err: any) {
      console.error("Failed to fetch categories", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async (catId: string | number) => {
    try {
      setLoading(true);
      // endpoint: /master/sub-category/dropdown/category/{categoryId}
      const res = await Axios.get(
        `${api.master.sub_category.fetchSubCategoryDropdownByCategoryId}/${catId}`
      );
      const resp = res?.data;
      let list: any[] = [];
      if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.payload)) list = res.data.payload;
      else if (resp && typeof resp === "object") list = Object.values(resp);
      const normalized = (list || []).map((it: any) => ({
        id: it?.id ?? it?.sub_category_id ?? it?.value ?? it?.key ?? "",
        name:
          it?.name ?? it?.sub_category_name ?? it?.label ?? it?.sub_category_name ??
          (typeof it === "string" ? it : ""),
        raw: it,
      }));
      setSubCategories(normalized);
    } catch (err) {
      console.error("Failed to fetch subcategories", err);
      setSubCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (subCatId: string | number) => {
    try {
      setLoading(true);
      // endpoint: /master/item/dropdown/sub-category/{subCategoryId}
      const res = await Axios.get(
        `${api.master.item.itemDropdownBySubcategoryId}/${subCatId}`
      );
      const resp = res?.data;
      let list: any[] = [];
      if (Array.isArray(resp)) list = resp;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.data?.payload)) list = res.data.payload;
      else if (resp && typeof resp === "object") list = Object.values(resp);
      const normalized = (list || []).map((it: any) => {
        // unit may be nested object like { unit_id, unit_name, ... }
        const unitObj = it?.unit || it?.unit_name || null;
        const unit_name =
          (unitObj && typeof unitObj === "object" && (unitObj.unit_name || unitObj.unitName)) ||
          it?.unit_name ||
          it?.unit?.unit_name ||
          it?.unit?.unitName ||
          "";

        return {
          id: it?.id ?? it?.item_id ?? it?.value ?? it?.key ?? "",
          item_name: it?.item_name ?? it?.name ?? it?.label ?? "",
          item_code: it?.item_code ?? it?.code ?? "",
          unit_name,
          raw: it,
        };
      });
      setItems(normalized);
    } catch (err) {
      console.error("Failed to fetch items", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onCategoryChange = (_: any, val: any) => {
    formik.setFieldValue("category", val);
    formik.setFieldValue("subCategory", null);
    formik.setFieldValue("item", null);
    setItems([]);
    if (val?.id) {
      fetchSubCategories(val.id);
    }
  };

  const onSubCategoryChange = (_: any, val: any) => {
    formik.setFieldValue("subCategory", val);
    formik.setFieldValue("item", null);
    setItems([]);
    if (val?.id) {
      fetchItems(val.id);
    }
  };

  const onItemChange = (_: any, val: any) => {
    formik.setFieldValue("item", val);
  };

  async function handleSubmit(values: FormValues) {
    try {
      const payload = {
        master_item_id: values.item?.id,
        reorder_type: "INDIVIDUAL",
        reorder_level: Number(values.reorderLevel),
      };

      await Axios.post(`${api.Inventory.Reorderlevel.ItemWise.create}`, payload);
      await showSuccess("Reorder level created successfully", "Created");
      formik.resetForm();
      setSubCategories([]);
      setItems([]);
      onCreated && onCreated();
      onClose();
    } catch (err: any) {
      console.error("Create reorder level failed", err);
      const serverMsg =
        err?.response?.data?.message || err?.response?.data?.error || err?.message;
      const friendly = serverMsg || "Failed to create reorder level";
      showError(friendly, "Error");
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Reorder Level</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="create-reorder-modal">
          <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
            <Row>
              {/* Category */}
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  size="small"
                  options={categories}
                  getOptionLabel={(option: any) => (option ? option.name : "")}
                  value={formik.values.category}
                  onChange={onCategoryChange}
                  onBlur={() => formik.setFieldTouched("category", true)}
                  disabled={loading}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Category"
                      error={formik.touched.category && Boolean(formik.errors.category)}
                      helperText={
                        formik.touched.category && formik.errors.category ? (
                          <span>{(() => {
                            const error: any = formik.errors.category;
                            if (typeof error === "string") return error as string;
                            if (Array.isArray(error)) return (error[0] || "") as string;
                            return String(error || "") as string;
                          })()}</span>
                        ) : null
                      }
                      fullWidth
                      size="small"
                      autoComplete="off"
                      sx={{ ...autocompleteDropdownStyles.inputSx }}
                      InputProps={{ ...params.InputProps, style: commonInputStyle }}
                      InputLabelProps={{ ...params.InputLabelProps, style: commonLabelStyle }}
                    />
                  )}
                />
              </Col>

              {/* Sub Category */}
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Sub Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  size="small"
                  options={subCategories}
                  getOptionLabel={(option: any) => (option ? option.name : "")}
                  value={formik.values.subCategory}
                  onChange={onSubCategoryChange}
                  onBlur={() => formik.setFieldTouched("subCategory", true)}
                  disabled={!formik.values.category || loading}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Sub Category"
                      error={formik.touched.subCategory && Boolean(formik.errors.subCategory)}
                      helperText={
                        formik.touched.subCategory && formik.errors.subCategory ? (
                          <span>{(() => {
                            const error: any = formik.errors.subCategory;
                            if (typeof error === "string") return error as string;
                            if (Array.isArray(error)) return (error[0] || "") as string;
                            return String(error || "") as string;
                          })()}</span>
                        ) : null
                      }
                      fullWidth
                      size="small"
                      autoComplete="off"
                      sx={{ ...autocompleteDropdownStyles.inputSx }}
                      InputProps={{ ...params.InputProps, style: commonInputStyle }}
                      InputLabelProps={{ ...params.InputLabelProps, style: commonLabelStyle }}
                    />
                  )}
                />
              </Col>

              {/* Item */}
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Item <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  size="small"
                  options={items}
                  getOptionLabel={(option: any) => (option ? option.item_name : "")}
                  value={formik.values.item}
                  onChange={onItemChange}
                  onBlur={() => formik.setFieldTouched("item", true)}
                  disabled={!formik.values.subCategory || loading}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Item"
                      error={formik.touched.item && Boolean(formik.errors.item)}
                      helperText={
                        formik.touched.item && formik.errors.item ? (
                          <span>{(() => {
                            const error: any = formik.errors.item;
                            if (typeof error === "string") return error as string;
                            if (Array.isArray(error)) return (error[0] || "") as string;
                            return String(error || "") as string;
                          })()}</span>
                        ) : null
                      }
                      fullWidth
                      size="small"
                      autoComplete="off"
                      sx={{ ...autocompleteDropdownStyles.inputSx }}
                      InputProps={{ ...params.InputProps, style: commonInputStyle }}
                      InputLabelProps={{ ...params.InputLabelProps, style: commonLabelStyle }}
                    />
                  )}
                />
              </Col>

              {/* Item Code (Read-only) */}
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>Item Code</Form.Label>
                <Form.Control
                  value={formik.values.item?.item_code || ""}
                  readOnly
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
              </Col>

              {/* Unit (Read-only) */}
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>Unit</Form.Label>
                <Form.Control
                  value={
                    (formik.values.item?.raw?.unit?.unit_name) ||
                    formik.values.item?.unit_name ||
                    ""
                  }
                  readOnly
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
              </Col>

              {/* Reorder Level */}
              <Col md={6} className="mb-3">
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Reorder Level <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="reorderLevel"
                  placeholder="Enter Reorder Level"
                  value={formik.values.reorderLevel}
                  onChange={(e: any) => formik.setFieldValue("reorderLevel", e.target.value)}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.reorderLevel && formik.touched.reorderLevel}
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid" style={{ color: "#dc3545" }}>
                  {formik.touched.reorderLevel && formik.errors.reorderLevel}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={formik.isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => formik.handleSubmit()}
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? "Saving..." : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateReorderLevelModal;
